/**
 * Team Management Service
 * Handles API calls for team member management, invites, and permissions
 */

import { BACKEND_URL } from '../config/api';
import { getAuthHeaders } from '../utils/auth';

const API_BASE = `${BACKEND_URL}/api/team`;

// Helper for authenticated requests with account context - uses shared utility
const authenticatedFetch = async (url, options = {}) => {
  const headers = {
    ...getAuthHeaders(),
    ...options.headers,
  };
  
  return fetch(url, { ...options, headers });
};

// ============================================================================
// TEAM MEMBERS
// ============================================================================

/**
 * Get all team members for the current account
 */
export const getTeamMembers = async () => {
  const response = await authenticatedFetch(`${API_BASE}/members`);
  if (!response.ok) {
    throw new Error('Failed to fetch team members');
  }
  return response.json();
};

/**
 * Update a team member's role
 */
export const updateTeamMember = async (membershipId, updates) => {
  const response = await authenticatedFetch(`${API_BASE}/members/${membershipId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update team member');
  }
  return response.json();
};

/**
 * Revoke a team member's access
 */
export const revokeTeamMember = async (membershipId, reason) => {
  const response = await authenticatedFetch(`${API_BASE}/members/${membershipId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to revoke team member');
  }
  return response.json();
};

// ============================================================================
// INVITES
// ============================================================================

/**
 * Send an invite to a new team member
 */
export const inviteTeamMember = async (email, role, customScopes = null) => {
  const response = await authenticatedFetch(`${API_BASE}/invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role, customScopes }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to send invite');
  }
  return response.json();
};

/**
 * Get invite info (public - no auth required)
 */
export const getInviteInfo = async (token) => {
  const response = await fetch(`${API_BASE}/invite/info?token=${encodeURIComponent(token)}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Invite not found');
  }
  return response.json();
};

/**
 * Accept an invite
 */
export const acceptInvite = async (token) => {
  const response = await authenticatedFetch(`${API_BASE}/invite/accept`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to accept invite');
  }
  return response.json();
};

/**
 * Sign up via invite (creates account and accepts invite in one step)
 * No email verification needed - user proved ownership by clicking the link
 */
export const signupViaInvite = async (token, firstName, lastName, password) => {
  const response = await fetch(`${API_BASE}/invite/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, firstName, lastName, password }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create account');
  }
  return response.json();
};

/**
 * Resend an invite
 */
export const resendInvite = async (membershipId) => {
  const response = await authenticatedFetch(`${API_BASE}/invite/${membershipId}/resend`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to resend invite');
  }
  return response.json();
};

// ============================================================================
// ROLES & PERMISSIONS
// ============================================================================

/**
 * Get current user's membership/role for this account
 */
export const getCurrentMembership = async () => {
  const response = await authenticatedFetch(`${API_BASE}/me`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch membership');
  }
  return response.json();
};

/**
 * Get available role presets
 */
export const getRolePresets = async () => {
  const response = await authenticatedFetch(`${API_BASE}/roles`);
  if (!response.ok) {
    throw new Error('Failed to fetch role presets');
  }
  return response.json();
};

// ============================================================================
// ACTIVITY LOGS
// ============================================================================

/**
 * Get activity logs for the account
 */
export const getActivityLogs = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.limit) params.append('limit', options.limit);
  if (options.offset) params.append('offset', options.offset);
  if (options.action) params.append('action', options.action);
  if (options.entityType) params.append('entityType', options.entityType);

  const response = await authenticatedFetch(`${API_BASE}/activity?${params.toString()}`);
  if (!response.ok) {
    throw new Error('Failed to fetch activity logs');
  }
  return response.json();
};

/**
 * Get all accounts the current user has access to
 */
export const getUserAccounts = async () => {
  const response = await authenticatedFetch(`${API_BASE}/accounts`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get accounts');
  }
  const data = await response.json();
  return data.accounts;
};

/**
 * Switch to a different account
 */
export const switchAccount = async (accountId) => {
  const response = await authenticatedFetch(`${API_BASE}/accounts/switch/${accountId}`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to switch account');
  }
  return response.json();
};

export default {
  getTeamMembers,
  updateTeamMember,
  revokeTeamMember,
  inviteTeamMember,
  getInviteInfo,
  acceptInvite,
  signupViaInvite,
  resendInvite,
  getCurrentMembership,
  getRolePresets,
  getActivityLogs,
  getUserAccounts,
  switchAccount,
};
